import React, { useState } from 'react';
import { css } from '@emotion/css';
import appLabels from '../../utils/appLabels';
import formatString, {formatPrice} from '../../utils/formatString';
import OrderDate from './OrderDate';
import { get, has, invoke, size } from 'lodash';
import moment from 'moment';
import OrderInfoIcon from './OrderInfoIcon';
import AppLabels from '../../utils/appLabels';
import ExpandOrderButton from './ExpandOrderButton';
import ScheduledItemsList from '../ScheduledItems/ScheduledItemsList';
import OrderAssetList from './OrderAssetList';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

const tileStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  box-sizing: border-box;

  @media (max-width: 641px) {
    width: initial;
    max-width: initial;
  }
`;

const DateSection = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0.75rem 0.75rem 0;
  background-color: ${(props: { expanded: boolean }) => (props.expanded ? '#FAF9F8' : 'initial')};
`;

const itemTextStyle = css`
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25rem;
`;

const editableDateStyle = css`
  font-weight: 400;
  font-size: 0.625rem;
  line-height: 1rem;
`;
const dateTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 0.75rem;
  flex: 1;
`;

const infoIconStyle = css``;

const expandedTileStyle = css`
  width: 100% !important;
  max-width: initial;
`;

const midSectionStyle = css`
  padding: 0 1rem;
`;

const bottomSectionStyle = css`
  display: flex;
  justify-content: space-between;
  padding: 0 0.75rem 0.75rem;

  @media (max-width: 641px) {
    flex-direction: column-reverse;
  }
`;

const manageLinkStyle = css`
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-decoration-line: underline !important;
  color: #7b1a27 !important;
  cursor: pointer;
  flex: 1;
  margin-right: 0.3rem;
  background: white;
  border: none;
  text-align: start;
  padding: 0;
`;

const totalsContainerStyle = css`
  flex: 1;
  padding-left: 0.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 641px) {
    padding-left: 0;
  }
`;

const totalsHeadingStyle = css`
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.463rem;
  padding-bottom: 0.625rem;
`;

const totalEstimatedSavingsPrice = css`
  color: #04633c;
  font-weight: 600;
`;

const subtotalsContainerStyle = css``;

const totalsContainerStyleExpanded = css`
  padding: 1rem 1.5rem 0;

  @media (max-width: 641px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const subTotalStyle = css`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;

  svg {
    margin-right: 0.2rem;
  }

  & p {
    margin: 0;
  }

  & > .label {
    flex: 1;
  }

  & > .green {
    color: #0c7d50;
  }
`;

const collapesedInfoIconStyle = css`
  margin-right: 0.2rem;
`;

const subTotalStyleExpanded = css`
  & > .amount {
    font-size: 1rem;
    @media (max-width: 641px) {
      font-size: 1rem;
    }
    @media (max-width: 425px) {
      font-size: 14px;
    }
  }
  & > .label {
    @media (max-width: 425px) {
      font-size: 14px;
    }
  }
  & > .original {
    color: #4b4b49;
    text-decoration: line-through;
    margin-right: 0.25rem;
  }
`;

const subTotalStyleCollapsed = css`
  font-size: 0.875rem;
  width: 100%;

  & .next-order-amount-padding {
    margin-left: 0.3rem;
    margin-top: initial;
    margin-bottom: initial;
  }

  .amount:last-child {
    margin-left: 0.3rem;
  }
`;

const termsStyle = css`
  white-space: break-spaces;
  flex-basis: 60%;
  padding: 1rem 0;
`;

const flexRow = css`
  display: flex;
  align-items: center;
`;


const estimatedSavingsStyle = css`
  display: flex;
  flex-direction: column;
  color: #04633c;
  & > div {
    display: flex;
    text-align: center;
    padding-left: 0.5rem;
    justify-content: space-between;
    margin-top: 0.5rem;
    @media (max-width: 425px) {
      & > .label {
        font-size: 14px;
      }
    }
    & > p {
      margin: 0;
      @media (max-width: 425px) {
        font-size: 14px;
      }
    }
  }
  & > .label {
    flex: 1;
  }
`;

const strokeStyle = css`
  height: 0.0625rem;
  background: #f1f0ee;
  margin: 0.75rem 0 0.5rem;
`;

const svgStyle = css`
  svg {
    margin-left: 0.2rem;
  }
`;

const FutureOrderContent = (props) => {
  const futureOrder = get(props, 'futureOrder', {});
  const cardSavings = get(futureOrder, 'cardSavings', []);
  const { savingsCategoryName = '', savingsAmount = 0.0 } = cardSavings[0] || {};
  const loading = get(props, 'loading', false);
  const [expanded, setExpanded] = useState(get(props, 'expanded', false));

  const itemsText = formatString(appLabels.FutureOrderContent.itemsText, {
    numberOfItems: get(futureOrder, 'numberOfItems'),
    multiItem: get(futureOrder, 'numberOfItems') > 1 ? 's' : '',
    deliveryType: get(futureOrder, 'fulfillmentType') === 'DUG' ? 'pickup' : 'delivery',
  });

  const editDate = loading ? moment('2024-01-01') : moment(get(futureOrder, 'orderEditDate'));
  const editableDate = formatString(appLabels.FutureOrderContent.editableText, {
    dayOfWeek: editDate.format('ddd'),
    month: editDate.format('MMM'),
    dayOfMonth: editDate.format('DD'),
  });

  const editableDateLabel = formatString(appLabels.FutureOrderContent.editableText, {
    dayOfWeek: editDate.format('dddd'),
    month: editDate.format('MMM'),
    dayOfMonth: editDate.format('DD'),
  });

  const subtotalLabel = formatString(appLabels.PendingOrderContent.estimatedTextLabel, {
    subtotal: get(futureOrder, 'estimatedSubTotal'),
  });

  const estimatedSubTotalItems = formatString(appLabels.FutureOrderContent.estimatedSubTotalItems, {
    totalItems: get(futureOrder, 'numberOfItems', 0),
    item: get(futureOrder, 'numberOfItems', 0) > 1 ? 'items' : 'item',
  });

  // if we pass a prop we use that instead, if not, we use the internal state
  const _expanded = has(props, 'expanded') ? get(props, 'expanded') : expanded;

  return (
    <div className={`${tileStyle} ${_expanded ? expandedTileStyle : ''}`}>
    <DateSection expanded={_expanded}>
      <OrderDate orderDate={get(futureOrder, 'orderDate')} />
      <div className={dateTextStyle}>
        <div className={itemTextStyle}>
          <span>{itemsText}</span>
        </div>
        <div className={editableDateStyle} aria-label={editableDateLabel}>
          <span aria-hidden={true}>{editableDate}</span>
        </div>
      </div>
      <ExpandOrderButton
        expanded={_expanded}
        onChange={(value) => {
          setExpanded(value);
          invoke(props, 'onExpand', value);
        }}
      />
    </DateSection>
    <div className={midSectionStyle}>
      {_expanded ? (
        <ScheduledItemsList
          cancellationReasons={[]}
          items={get(futureOrder, 'orderItems')}
          showSkipOnceLink={false}
          showHeaders={false}
          editableQuantity={false}
          displayItemFrequency={false}
          displaySubstitutionCheckbox={false}
          displayCancelButton={false}
          orderType="future"
        />
      ) : (
        <OrderAssetList products={get(futureOrder, 'orderItems')} loading={loading} />
      )}
    </div>
    <div className={bottomSectionStyle}>
      {!_expanded ? (
        <>
          <div className={`${subTotalStyle} ${subTotalStyleCollapsed}`}>
            <div className={`${_expanded ? 'body-text' : 'body-text-sm'} label`}>
              <div className={flexRow}>{<div className={manageLinkStyle}></div>}</div>
            </div>
            <OrderInfoIcon
              className={collapesedInfoIconStyle}
              popoverContent={AppLabels.FutureOrderContent.SubtotalInfo}
              analyticsPageView={'futureOrder'}
              label={AppLabels.FutureOrderContent.estimatedTooltipLabel}
              height={16}
              width={16}
            />
            <div className={flexRow} aria-label={subtotalLabel}>
              <p className={`${_expanded ? 'body-text' : 'body-text-sm'} amount`}>
                <span aria-hidden={true}>{appLabels.FutureOrderContent.estimatedText}</span>
              </p>
              <p className={`${_expanded ? 'body-text' : 'body-text-sm'} amount`} aria-hidden={true}>
                <span>${get(futureOrder, 'estimatedSubTotal')}</span>
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={termsStyle}>
            <span className="body-text-xs">{AppLabels.FutureOrderContent.terms}</span>
          </div>
          <div className={`${totalsContainerStyle} ${totalsContainerStyleExpanded}`}>
            <div className={totalsHeadingStyle}>Order summary</div>
            <div className={expanded ? subtotalsContainerStyle : ''}>
              <div className={`${subTotalStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}>
                <div className={`body-text label`}>
                  <div className={flexRow}>{estimatedSubTotalItems}</div>
                </div>
                <p className={`body-text amount`}>${get(futureOrder, 'estimatedSubTotal', '0.00')}</p>
              </div>
              {!!size(cardSavings) && (
                <>
                  <div className={`${subTotalStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}>
                    <div className={`body-text label`}>
                      <div className={flexRow}>{appLabels.FutureOrderContent.estimatedSavings}</div>
                    </div>
                    <p className={`${totalEstimatedSavingsPrice} body-text amount`}>-${formatPrice(savingsAmount)}</p>
                  </div>

                  <div className={estimatedSavingsStyle}>
                    <div>
                      <div className="body-text label">{savingsCategoryName}</div>
                      <p className="body-text amount">-${formatPrice(savingsAmount)}</p>
                    </div>
                  </div>
                </>
              )}

              <>
                <div className={strokeStyle} />
                <div
                  className={`${subTotalStyle} ${svgStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}
                >
                  <div className={`${expanded ? 'body-text' : 'body-text-sm'} label`}>
                    <div className={flexRow}>
                      {appLabels.FutureOrderContent.estimatedTotalBeforeTax}
                      <OrderInfoIcon
                        className={infoIconStyle}
                        width={16}
                        popoverContent={AppLabels.PendingOrderContent.SubtotalInfo}
                        analyticsPageView={''}
                        label={appLabels.FutureOrderContent.estimatedTooltipLabel}
                      />
                    </div>
                  </div>
                  {!!savingsAmount && (
                    <p className={`${expanded ? 'body-text' : 'body-text-sm'} amount original`}>
                      ${get(futureOrder, 'estimatedSubTotal', 0)}
                    </p>
                  )}
                  <p className={`${expanded ? 'body-text' : `body-text-sm`} amount`}>
                    ${get(futureOrder, 'discountedEstSubTotal', 0)}
                  </p>
                </div>
              </>
            </div>
          </div>
        </>
 )}
    </div>
  </div>
  );
};

FutureOrderContent.propTypes = {
  loading: PropTypes.bool,
  futureOrder: PropTypes.object,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func,
};
export default FutureOrderContent;
